import React from 'react';
import type { ButtonSize } from '@wix/design-system';
import { Button, Loader, Tooltip } from '@wix/design-system';
import {
  addAppClick,
  appMarketAppInstallationFailedClient,
  appMarketAppInstallationSuccess,
} from '@wix/bi-logger-app-market-data/v2';
import { useTranslation } from '../../../../contexts/translations-context';
import { InstallAppButtonDataHooks } from './data-hooks';
import {
  useEssentials,
  useInstaller,
  useJunkYard,
  useServices,
  useUserContext,
} from '../../../../contexts';
import {
  getAddAppClickBiData,
  getAppMarketAppInstallationFailedClientBiData,
  getAppMarketAppInstallationSuccessBiData,
} from '../../../../bi-services/events-data-parser';
import type { AppBadge, SupportedAppStatuses } from '@wix/app-market-services';
import { useQueryClient } from '@tanstack/react-query';
import { QueryDecorator } from '../../../../decorators';
import { CustomEvent } from '@wix/app-market-analytics';
import { initGoogleAnalytics } from '../../../../methods/init-google-analytics';
import { v4 as generateUuidV4 } from 'uuid';

export interface InstallAppButtonProps {
  origin: string;
  isDisabled: boolean;
  tooltipContent: string;
  app: {
    id: string;
    isAppWithBenefits: boolean;
    appBadges?: AppBadge[];
    gTag?: string;
  };
  size: ButtonSize;
  status: SupportedAppStatuses;
  isTestMode?: boolean;
  openMobileInstallationBlockedModal: () => void;
}
export function InstallAppButton({
  origin,
  isDisabled,
  tooltipContent,
  app,
  status,
  isTestMode,
  size,
  openMobileInstallationBlockedModal,
}: InstallAppButtonProps) {
  const { t } = useTranslation();
  const { isMobile } = useUserContext();
  const installer = useInstaller();
  const queryClient = useQueryClient();
  const services = useServices();
  const { biLogger, googleAnalytics } = useEssentials();
  const { routerData, goBackToReferrer } = useJunkYard();

  return (
    <QueryDecorator
      queryFn={async () => {
        const webSolutionsBase = await services.getWebSolutionsBase({
          appIds: [app.id],
        });
        const webSolutionBase = webSolutionsBase.findByAppId(app.id);
        return webSolutionBase?.openConsentMethod;
      }}
      queryKey={`install-app-button-${app.id}`}
      csrOnly
    >
      {({ data: openConsentMethod, isLoading }) => {
        const correlationId = generateUuidV4();
        return (
          <Tooltip
            dataHook={InstallAppButtonDataHooks.tooltip}
            inline
            disabled={!tooltipContent}
            content={tooltipContent}
          >
            <Button
              dataHook={InstallAppButtonDataHooks.button}
              disabled={isDisabled || isLoading}
              size={size}
              onClick={() => {
                biLogger.report(
                  addAppClick(
                    getAddAppClickBiData({
                      appId: app.id,
                      tagType: 'app_page',
                      tagName: app.id,
                      routerData,
                      referrerNameForBI: goBackToReferrer?.referrerNameForBI,
                      appBadges: app.appBadges,
                      origin,
                      correlationId,
                      marketMode:
                        status === 'PUBLISHED'
                          ? 'public'
                          : status === 'APPROVED'
                          ? 'unlisted'
                          : 'test_my_app',
                    }),
                  ),
                );
                const gaInstance = initGoogleAnalytics(
                  googleAnalytics,
                  app.gTag,
                );

                gaInstance?.reportCustomEvent({
                  name: CustomEvent.INSTALL_CLICK,
                  category: 'User Engagement',
                });

                if (isMobile) {
                  return openMobileInstallationBlockedModal();
                }

                installer.installApp({
                  appId: app.id,
                  consentOpeningMethod: openConsentMethod ?? 'NEW_TAB',
                  onSuccess: (instanceId) => {
                    queryClient.invalidateQueries();
                    biLogger.report(
                      appMarketAppInstallationSuccess(
                        getAppMarketAppInstallationSuccessBiData({
                          appId: app.id,
                          tagType: 'app_page',
                          tagName: app.id,
                          instanceId,
                          routerData,
                          referrerNameForBI:
                            goBackToReferrer?.referrerNameForBI,
                          appBadges: app.appBadges,
                          origin,
                          correlationId,
                        }),
                      ),
                    );
                  },
                  onFailure: () => {
                    biLogger.report(
                      appMarketAppInstallationFailedClient(
                        getAppMarketAppInstallationFailedClientBiData({
                          appId: app.id,
                          tagType: 'app_page',
                          tagName: app.id,
                        }),
                      ),
                    );
                  },
                  correlationId,
                });
              }}
            >
              {isLoading ? (
                <Loader size="tiny" />
              ) : isTestMode ? (
                t('solution.page.addToSiteDevVersion')
              ) : (
                t('solution.page.addToSite')
              )}
            </Button>
          </Tooltip>
        );
      }}
    </QueryDecorator>
  );
}
